import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { FormInputOtp } from '../../inputs/FormInputOtp';
import { useTimer } from '../../../hooks/useTimer';
import { TextButton } from '../../commons/buttons/TextButton';
import { ApiError } from '../../../models/ApiError';
import { useAppDispatch } from '../../../hooks/redux';
import { authSlice } from '../../../store/reducers/AuthSlice';
import { ButtonClassNames, GlobalRouteNames } from '../../../models/Common';
import { authorizationApi } from '../../../services/apiServices/user/authorization';

interface EnterCodeSms {
  handleBack: () => void;
}

interface CredentialWithCode extends Credential {
  code?: string;
}

export const EnterCodeSms: FC<EnterCodeSms> = ({ handleBack }) => {
  const [enterCodeSms, { isLoading: isEnteredCodeLoading }] = authorizationApi.useEnterCodeSmsMutation();
  const [requestCodeSms, { isLoading: isRequestCodeLoading }] = authorizationApi.useRequestCodeSmsMutation();
  const { setCredentials } = authSlice.actions;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { timer, startTimer, resetTimer } = useTimer(60);
  const { t } = useTranslation();
  const methods = useForm();

  const {
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = methods;

  const submitEnterCodeSms = async (smsCode: { smsCode: string }) => {
    if (localStorage.getItem('accountPhone')) {
      try {
        await enterCodeSms({ auth_id: localStorage.getItem('accountPhone') || '', sms_code: smsCode.smsCode })
          .unwrap()
          .then((credentials) => {
            dispatch(
              setCredentials({ accessToken: credentials.access_token, refreshToken: credentials.refresh_token }),
            );
            history.push(GlobalRouteNames.HomePage);
          });
      } catch (err) {
        const error = err as ApiError;
        setError('smsCode', {
          type: 'server',
          message: error?.data?.error?.message,
        });
      }
    } else {
      setError('smsCode', {
        type: 'local',
        message: t('validation.numberNotSaved'),
      });
    }
  };

  const handleResendCode = async () => {
    if (localStorage.getItem('accountPhone')) {
      try {
        await requestCodeSms(localStorage.getItem('accountPhone') || '')
          .unwrap()
          .then(() => resetTimer());
      } catch (err) {
        const error = err as ApiError;
        setError('smsCode', {
          type: 'server',
          message: error?.data?.error?.message,
        });
      }
    } else {
      setError('smsCode', {
        type: 'local',
        message: t('validation.numberNotSaved'),
      });
    }
  };

  useEffect(() => {
    startTimer();

    if ('OTPCredential' in window) {
      const ac = new AbortController();
      const options = { otp: { transport: ['sms'] }, signal: ac.signal };
      navigator.credentials
        .get(options)
        .then((otp: CredentialWithCode | null) => {
          setValue('smsCode', otp?.code);
          ac.abort();
        })
        .catch(() => {
          ac.abort();
        });
    }
  }, []);

  return (
    <div>
      <div className="login-enter-code-sms-container">
        <p className="login-enter-code-sms-container__title login-page__title">{t('auth.authWithSms')}</p>
        <p className="login-enter-code-sms-container__text">{t('auth.enterCodeFromSms')}</p>
        <FormProvider {...methods}>
          <form className="login-enter-code-sms-container__form" onSubmit={handleSubmit(submitEnterCodeSms)}>
            <FormInputOtp
              name="smsCode"
              inputClassName="primary"
              containerClassName="login-enter-code-sms-container__form__otp"
              isRequired
              minMaxLength={4}
            />
            {errors.smsCode?.message && (
              <p className="login-enter-code-sms-container__form__error">{errors.smsCode?.message}</p>
            )}
            <div className="login-enter-code-sms-container__form__btn-group">
              <CustomMUIButton
                label={t('common.back')}
                handleClick={handleBack}
                className={ButtonClassNames.SecondaryOutlined}
                isBold
              />
              <CustomMUIButton
                label={t('auth.logIn')}
                className={ButtonClassNames.PrimaryFulfilled}
                type="submit"
                isBold
                isLoading={isEnteredCodeLoading || isRequestCodeLoading}
              />
            </div>

            <TextButton
              label={timer > 0 ? `${t('auth.sendCodeAgain')} (${timer})` : t('auth.sendCodeAgain')}
              handleClick={handleResendCode}
              className="primary"
              isArrow={false}
              isDisabled={timer > 0 || isEnteredCodeLoading || isRequestCodeLoading}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
